import get from 'lodash.get';
import isEmpty from 'lodash.isempty';

const MAX_NESTING_DEPTH = 4;
const DEFAULT_ERROR_MESSAGE_KEY = 'esl.error_500.oops_broken';
const TEXT_PROPERTY_NAMES = ['message', 'error_description', 'error'];

function getIsValidMessageKey(str) {
    if (!str || typeof str !== 'string') {
        return false;
    }
    const keyPattern = /^[^. ]+(\.[^. ]+)+$/;
    return keyPattern.test(str);
}

function getIsPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getIsFilledString(value) {
    return typeof value === 'string' && value.trim() !== '';
}

function getNestedNodes(node) {
    const nestedNodes = [];

    ['error', 'data'].forEach((propertyName) => {
        if (getIsPlainObject(node[propertyName])) {
            nestedNodes.push(node[propertyName]);
        }
    });

    const errors = node.errors;

    if (Array.isArray(errors)) {
        nestedNodes.push(...errors);
    } else if (getIsPlainObject(errors)) {
        Object.values(errors).forEach((fieldErrors) => nestedNodes.push(...[].concat(fieldErrors)));
    }

    return nestedNodes;
}

/**
 * Finds messageKey, message and parameters in an error body however deep the service nested them
 * (message, error, error.message, data.error, errors[].message, errors.field[0]); the shallowest wins.
 *
 * @param error - a thrown request error ({response: {data}}) or a synthetic one ({data})
 * @returns {{messageKey: string|null, message: string|null, parameters: Record<string, any>}}
 */
export const getErrorDetails = (error) => {
    const rootBody = get(error, 'response.data') || get(error, 'data');
    const details = {messageKey: null, message: null, parameters: null};
    let fieldMessage = null;
    let currentLevel = rootBody ? [rootBody] : [];

    for (let depth = 0; depth <= MAX_NESTING_DEPTH && currentLevel.length > 0; depth += 1) {
        const nextLevel = [];

        currentLevel.forEach((node) => {
            if (getIsFilledString(node)) {
                fieldMessage = fieldMessage || node;
                return;
            }

            if (!getIsPlainObject(node)) {
                return;
            }

            if (!details.messageKey && getIsFilledString(node.messageKey)) {
                details.messageKey = node.messageKey;
            }

            if (!details.parameters && getIsPlainObject(node.parameters)) {
                details.parameters = node.parameters;
            }

            if (!details.message) {
                details.message = TEXT_PROPERTY_NAMES.map((name) => node[name]).find(getIsFilledString) || null;
            }

            nextLevel.push(...getNestedNodes(node));
        });

        currentLevel = nextLevel;
    }

    return {
        messageKey: details.messageKey,
        message: details.message || fieldMessage,
        parameters: details.parameters || {}
    };
};

export const getErrorMessage = (error, skipPrefix) => {
    if (isEmpty(error)) {
        return null;
    }

    const {messageKey: rawMessageKey, message, parameters} = getErrorDetails(error);

    const messageKey = (getIsValidMessageKey(rawMessageKey) && rawMessageKey)
        || message
        || rawMessageKey
        || 'error_500.oops_broken';

    const hasEslPrefix = messageKey.startsWith('esl.');
    const isPlainText = messageKey.includes(' ');

    if (skipPrefix || hasEslPrefix || isPlainText) {
        return {messageKey, message, parameters};
    }

    return {messageKey: `esl.${messageKey}`, message, parameters};
};

/**
 * The text to show for an error: the translation of its messageKey when the catalogue has one,
 * else the service's own message as is, else the generic error text.
 *
 * @param errorMessage - what getErrorMessage returned
 * @param intl - react-intl instance
 */
export const getErrorText = (errorMessage, intl) => {
    const {messageKey, message, parameters = {}} = errorMessage || {};
    const isPlainText = !messageKey || messageKey.includes(' ');
    const translatedKey = isPlainText
        ? null
        : [messageKey, `esl.${messageKey}`, messageKey.replace(/^esl\./, '')].find((key) => intl.messages?.[key]);

    if (translatedKey) {
        return intl.formatMessage({id: translatedKey}, parameters);
    }

    const text = message || (isPlainText ? messageKey : '');

    return text || intl.formatMessage({id: DEFAULT_ERROR_MESSAGE_KEY, defaultMessage: ''});
};

/**
 * Adds the esl. namespace to a key unless it already has it or is plain text.
 *
 * @param messageKey - a message key as the backend sent it or as getErrorMessage built it
 */
export const getPrefixedMessageKey = (messageKey) => {
    const isAlreadyResolved = Boolean(messageKey) && (messageKey.startsWith('esl.') || messageKey.includes(' '));

    return isAlreadyResolved ? messageKey : `esl.${messageKey}`;
};

export const getErrorCode = (error) => get(error, 'data.code', null);

const EXTRACTION_FAILED_PREFIX = 'Document extraction failed:';
const RECIPIENT_PLACEHOLDER_EXISTS_PATTERN = /placeholder (\S+) already exists/;

export const getDocumentExtractionError = (error) => {
    const responseData = get(error, 'response.data') || get(error, 'data') || {};
    const message = responseData.message || '';

    if (!message.startsWith(EXTRACTION_FAILED_PREFIX)) {
        return getErrorMessage(error);
    }

    const separatorIdx = message.indexOf(' : ');
    if (separatorIdx === -1) {
        return getErrorMessage(error);
    }

    const jsonObjectStart = message.indexOf('{', separatorIdx);
    const jsonObjectEnd = message.lastIndexOf('}');

    if (jsonObjectStart === -1 || jsonObjectEnd <= jsonObjectStart) {
        return getErrorMessage(error);
    }

    try {
        const innerData = JSON.parse(message.slice(jsonObjectStart, jsonObjectEnd + 1));
        const tagErrors = get(innerData, 'error.tags') || {};
        const globalErrors = get(innerData, 'error.global') || [];

        if (globalErrors.length > 0 || isEmpty(tagErrors)) {
            return getErrorMessage(error);
        }

        const errorEntries = Object.entries(tagErrors);
        const recipientExistsEntries = errorEntries.filter(([, errors]) =>
            errors.some((msg) => RECIPIENT_PLACEHOLDER_EXISTS_PATTERN.test(msg))
        );

        if (recipientExistsEntries.length < errorEntries.length) {
            return getErrorMessage(error);
        }

        const placeholders = [...new Set(
            recipientExistsEntries.flatMap(([, errors]) =>
                errors
                    .map((msg) => {
                        const match = RECIPIENT_PLACEHOLDER_EXISTS_PATTERN.exec(msg);
                        return match ? match[1] : null;
                    })
                    .filter(Boolean)
            )
        )];

        return {
            messageKey: 'esl.error.validation.texttag.recipientAlreadyExists',
            parameters: {tag: placeholders.join(', ')}
        };
    } catch (e) {
        return getErrorMessage(error);
    }
};

export const getLocalizedErrorMessage = (messageKey, intl) => getErrorText({messageKey}, intl);
